import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	LinearProgress,
	IconButton,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	CircularProgress,
	Alert,
	Chip,
} from "@mui/material";
import { Upload, X, FileText, Download, Trash2, RefreshCw } from "lucide-react";
import { apiClient } from "../../services/api";

const ManualTrackingModal = ({ open, onClose }) => {
	const [files, setFiles] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [existingFiles, setExistingFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);

	// Fetch existing files when modal opens
	useEffect(() => {
		if (open) {
			fetchExistingFiles();
		}
	}, [open]);
	const fetchExistingFiles = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiClient.get("/api/manual-tracking/files");
			setExistingFiles(response.data.files || []);
		} catch (err) {
			console.error("Error fetching files:", err);
			setError("Failed to load existing files");
		} finally {
			setLoading(false);
		}
	};

	const handleFileSelect = (event) => {
		const selectedFiles = Array.from(event.target.files);
		const excelFiles = selectedFiles.filter((file) => {
			const extension = file.name.split(".").pop().toLowerCase();
			return ["xlsx", "xls", "xlsm"].includes(extension);
		});

		if (excelFiles.length < selectedFiles.length) {
			setError("Only Excel files (.xlsx, .xls, .xlsm) are allowed");
		} else {
			setError(null);
		}

		setFiles(excelFiles);
	};
	const handleUpload = async () => {
		if (files.length === 0) return;

		// Get the username from localStorage (lowercase 'username' is used throughout the app)
		const userName = localStorage.getItem("username") || "Unknown User";

		setUploading(true);
		setUploadProgress(0);
		setError(null);
		setSuccess(null);

		try {
			const formData = new FormData();

			// Log file sizes and compute checksums before upload for verification
			const fileChecksums = {};
			for (const file of files) {
				console.log(
					`📤 Uploading file: ${file.name}, size: ${file.size} bytes`,
				);

				// Compute client-side SHA-256 for verification
				try {
					const arrayBuffer = await file.arrayBuffer();
					const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
					const hashArray = Array.from(new Uint8Array(hashBuffer));
					const hashHex = hashArray
						.map((b) => b.toString(16).padStart(2, "0"))
						.join("");
					fileChecksums[file.name] = {
						size: file.size,
						sha256: hashHex,
					};
					console.log(`🔒 Computed SHA-256 for ${file.name}: ${hashHex}`);
				} catch (err) {
					console.warn("⚠️ Could not compute checksum:", err);
				}

				formData.append("files", file);
			}

			// Add the username to track who uploaded/modified
			formData.append("uploaded_by", userName);

			const response = await apiClient.post(
				"/api/manual-tracking/upload",
				formData,
				{
					onUploadProgress: (progressEvent) => {
						const percentCompleted = Math.round(
							(progressEvent.loaded * 100) / progressEvent.total,
						);
						setUploadProgress(percentCompleted);
					},
					// Ensure binary transfer without transformation
					headers: {
						Accept: "application/json",
					},
				},
			);

			console.log("✅ Upload response:", response.data);

			// Verify checksums match
			if (response.data.files) {
				for (const uploadedFile of response.data.files) {
					const clientChecksum = fileChecksums[uploadedFile.filename];
					if (clientChecksum && uploadedFile.sha256) {
						if (clientChecksum.sha256 === uploadedFile.sha256) {
							console.log(`✅ Checksum verified for ${uploadedFile.filename}`);
						} else {
							console.error(
								`❌ Checksum mismatch for ${uploadedFile.filename}!`,
							);
							console.error(`   Client: ${clientChecksum.sha256}`);
							console.error(`   Server: ${uploadedFile.sha256}`);
							setError(
								`Integrity check failed for ${uploadedFile.filename}. File may be corrupted.`,
							);
							return;
						}
					}
				}
			}

			// Show specific message if file was updated or uploaded
			const hasUpdates = response.data.files?.some(
				(f) => f.action === "updated",
			);
			if (hasUpdates) {
				setSuccess(
					`Successfully updated ${files.length} file(s). Changes are now visible to all users.`,
				);
			} else {
				setSuccess(`Successfully uploaded ${files.length} file(s)`);
			}

			setFiles([]);
			// Refresh the file list
			await fetchExistingFiles();
		} catch (err) {
			console.error("❌ Upload error:", err);
			setError(err.response?.data?.detail || "Failed to upload files");
		} finally {
			setUploading(false);
			setUploadProgress(0);
		}
	};
	const handleDownload = async (fileName) => {
		try {
			console.log(`📥 Downloading: ${fileName}`);

			// Request binary data with blob response type
			const response = await apiClient.get(
				`/api/manual-tracking/download/${encodeURIComponent(fileName)}`,
				{
					responseType: "blob",
					// Ensure no transformations are applied
					headers: {
						Accept: "*/*",
					},
				},
			);

			const blob = response.data;
			const downloadedSize = blob.size;
			console.log(`✅ Downloaded file size: ${downloadedSize} bytes`);

			// Check integrity if checksum is provided in headers
			const serverChecksum = response.headers["x-content-sha256"];
			const originalSize = response.headers["x-original-size"];

			if (originalSize && downloadedSize !== parseInt(originalSize)) {
				console.warn(
					`⚠️ Size mismatch! Expected: ${originalSize} bytes, Got: ${downloadedSize} bytes`,
				);
			} else if (originalSize) {
				console.log(
					`✅ Size verified: ${downloadedSize} bytes matches original`,
				);
			}

			if (serverChecksum) {
				console.log(`🔒 Server checksum: ${serverChecksum}`);

				// Optionally verify checksum on client side
				try {
					const arrayBuffer = await blob.arrayBuffer();
					const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
					const hashArray = Array.from(new Uint8Array(hashBuffer));
					const clientChecksum = hashArray
						.map((b) => b.toString(16).padStart(2, "0"))
						.join("");

					if (clientChecksum === serverChecksum) {
						console.log(`✅ Checksum verified! File integrity confirmed.`);
					} else {
						console.error(`❌ Checksum mismatch!`);
						console.error(`   Expected: ${serverChecksum}`);
						console.error(`   Got: ${clientChecksum}`);
						setError(
							`File integrity check failed for ${fileName}. File may be corrupted. Please try downloading again.`,
						);
						return;
					}
				} catch (err) {
					console.warn("⚠️ Could not verify checksum on client:", err);
				}
			}

			// Create a download link with the blob
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", fileName);
			document.body.appendChild(link);
			link.click();

			// Cleanup
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			console.log(`✅ Download completed: ${fileName}`);
		} catch (err) {
			console.error("❌ Download error:", err);
			setError("Failed to download file");
		}
	};
	const handleDelete = async (fileName) => {
		if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
			return;
		}
		try {
			await apiClient.delete(
				`/api/manual-tracking/delete/${encodeURIComponent(fileName)}`,
			);
			setSuccess(`Successfully deleted ${fileName}`);
			await fetchExistingFiles();
		} catch (err) {
			console.error("Delete error:", err);
			setError("Failed to delete file");
		}
	};

	const handleRemoveFile = (index) => {
		setFiles(files.filter((_, i) => i !== index));
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
	};

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleDateString() + " " + date.toLocaleTimeString();
	};

	const handleClose = () => {
		setFiles([]);
		setError(null);
		setSuccess(null);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: "0px",
					maxHeight: "80vh",
				},
			}}
		>
			<DialogTitle
				sx={{
					borderBottom: "1px solid #e0e0e0",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					fontFamily: "MAN Europe_Bold",
				}}
			>
				<span>Manual Tracking - Excel File Management</span>
				<IconButton onClick={handleClose} size="small">
					<X size={20} />
				</IconButton>
			</DialogTitle>{" "}
			<DialogContent sx={{ p: 3 }}>
				{/* Info Box - Collaborative Workflow */}
				<Alert severity="info" sx={{ mb: 2 }}>
					<Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
						📋 How Collaborative Editing Works:
					</Typography>
					<Typography variant="body2" component="div" sx={{ fontSize: "13px" }}>
						1. <strong>Download</strong> a file to edit it locally
						<br />
						2. Make your changes in Excel
						<br />
						3. <strong>Upload</strong> the file with the same name to update it
						<br />
						4. Other users click <strong>Refresh</strong> to see your changes
					</Typography>
				</Alert>
				{/* Alert Messages */}
				{error && (
					<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
						{error}
					</Alert>
				)}
				{success && (
					<Alert
						severity="success"
						sx={{ mb: 2 }}
						onClose={() => setSuccess(null)}
					>
						{success}
					</Alert>
				)}
				{/* Upload Section */}
				<Box sx={{ mb: 4 }}>
					<Typography
						variant="h6"
						sx={{ mb: 1, fontFamily: "MAN Europe_Bold", fontSize: "16px" }}
					>
						Upload or Update Files
					</Typography>
					<Typography
						variant="body2"
						sx={{ mb: 2, color: "#666", fontSize: "13px" }}
					>
						💡 Tip: Upload a file with the same name to update it. Everyone will
						see your changes after clicking Refresh.
					</Typography>

					<Box
						sx={{
							border: "2px dashed #ccc",
							borderRadius: "8px",
							padding: "30px",
							textAlign: "center",
							backgroundColor: "#fafafa",
							cursor: "pointer",
							transition: "all 0.2s",
							"&:hover": {
								borderColor: "#1e2832",
								backgroundColor: "#f5f5f5",
							},
						}}
						onClick={() => document.getElementById("file-input").click()}
					>
						<input
							id="file-input"
							type="file"
							multiple
							accept=".xlsx,.xls,.xlsm"
							style={{ display: "none" }}
							onChange={handleFileSelect}
						/>
						<Upload size={40} color="#666" style={{ marginBottom: "10px" }} />
						<Typography
							variant="body1"
							sx={{ mb: 1, fontFamily: "MAN Europe_Regular" }}
						>
							Click to select Excel files or drag and drop
						</Typography>
						<Typography variant="caption" sx={{ color: "#666" }}>
							Supported formats: .xlsx, .xls, .xlsm
						</Typography>
					</Box>

					{/* Selected Files for Upload */}
					{files.length > 0 && (
						<Box sx={{ mt: 2 }}>
							<Typography
								variant="subtitle2"
								sx={{ mb: 1, fontFamily: "MAN Europe_Bold" }}
							>
								Selected Files ({files.length})
							</Typography>
							<List sx={{ bgcolor: "#f9f9f9", borderRadius: "4px", p: 1 }}>
								{files.map((file, index) => (
									<ListItem key={index} sx={{ py: 1 }}>
										<FileText
											size={20}
											color="#1e2832"
											style={{ marginRight: "10px" }}
										/>
										<ListItemText
											primary={file.name}
											secondary={formatFileSize(file.size)}
											primaryTypographyProps={{ fontSize: "14px" }}
											secondaryTypographyProps={{ fontSize: "12px" }}
										/>
										<ListItemSecondaryAction>
											<IconButton
												edge="end"
												size="small"
												onClick={() => handleRemoveFile(index)}
											>
												<X size={16} />
											</IconButton>
										</ListItemSecondaryAction>
									</ListItem>
								))}
							</List>
						</Box>
					)}

					{/* Upload Progress */}
					{uploading && (
						<Box sx={{ mt: 2 }}>
							<LinearProgress variant="determinate" value={uploadProgress} />
							<Typography
								variant="caption"
								sx={{ mt: 1, display: "block", textAlign: "center" }}
							>
								Uploading... {uploadProgress}%
							</Typography>
						</Box>
					)}
				</Box>{" "}
				{/* Existing Files Section */}
				<Box>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 2,
						}}
					>
						<Typography
							variant="h6"
							sx={{ fontFamily: "MAN Europe_Bold", fontSize: "16px" }}
						>
							Shared Files
						</Typography>
						<Button
							startIcon={<RefreshCw size={16} />}
							onClick={fetchExistingFiles}
							disabled={loading}
							size="small"
							sx={{
								textTransform: "none",
								fontFamily: "MAN Europe_Bold",
								color: "#1976d2",
							}}
						>
							Refresh
						</Button>
					</Box>

					{loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
							<CircularProgress />
						</Box>
					) : existingFiles.length === 0 ? (
						<Typography
							variant="body2"
							sx={{ color: "#666", textAlign: "center", py: 4 }}
						>
							No files uploaded yet
						</Typography>
					) : (
						<List
							sx={{
								bgcolor: "#f9f9f9",
								borderRadius: "4px",
								maxHeight: "300px",
								overflow: "auto",
							}}
						>
							{existingFiles.map((file, index) => (
								<ListItem
									key={index}
									sx={{
										py: 1.5,
										borderBottom:
											index < existingFiles.length - 1
												? "1px solid #e0e0e0"
												: "none",
									}}
								>
									{" "}
									<FileText
										size={20}
										color="#1e2832"
										style={{ marginRight: "10px" }}
									/>
									<ListItemText
										primary={
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 1 }}
											>
												<span>{file.name}</span>
												{file.size && (
													<Chip
														label={formatFileSize(file.size)}
														size="small"
														sx={{ height: "20px", fontSize: "11px" }}
													/>
												)}
											</Box>
										}
										secondary={
											<Box>
												<div>
													{file.last_modified
														? `Modified: ${formatDate(file.last_modified)}`
														: ""}
												</div>
												{file.last_modified_by &&
													file.last_modified_by !== "Unknown" && (
														<div style={{ color: "#1976d2", fontWeight: 500 }}>
															Last edited by: {file.last_modified_by}
														</div>
													)}
											</Box>
										}
										primaryTypographyProps={{ fontSize: "14px" }}
										secondaryTypographyProps={{
											fontSize: "12px",
											component: "div",
										}}
									/>{" "}
									<ListItemSecondaryAction>
										<Box sx={{ display: "flex", gap: 1 }}>
											<IconButton
												edge="end"
												size="small"
												onClick={() => handleDownload(file.name)}
												title="Download file"
											>
												<Download size={16} />
											</IconButton>
											<IconButton
												edge="end"
												size="small"
												onClick={() => handleDelete(file.name)}
												title="Delete file"
												sx={{ color: "#cc0000" }}
											>
												<Trash2 size={16} />
											</IconButton>
										</Box>
									</ListItemSecondaryAction>
								</ListItem>
							))}
						</List>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ borderTop: "1px solid #e0e0e0", p: 2 }}>
				<Button
					onClick={handleClose}
					sx={{
						color: "#666",
						fontFamily: "MAN Europe_Bold",
						textTransform: "none",
					}}
				>
					Close
				</Button>
				<Button
					onClick={handleUpload}
					disabled={files.length === 0 || uploading}
					variant="contained"
					sx={{
						backgroundColor: "#1e2832",
						fontFamily: "MAN Europe_Bold",
						textTransform: "none",
						"&:hover": {
							backgroundColor: "#2c3a48",
						},
						"&:disabled": {
							backgroundColor: "#ccc",
						},
					}}
				>
					{uploading
						? "Uploading..."
						: `Upload ${files.length > 0 ? `(${files.length})` : ""}`}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ManualTrackingModal;
